import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import {
  AlertTriangle, ArrowRight, CheckCircle2, Clock,
  TrendingUp, ShieldCheck, Inbox,
} from "lucide-react";
import { NarrativeCard, ProgressBar, Pill } from "@/design/components";
import { formatPct, formatHumanCurrency } from "@/design/format";
import { useSeed, useProjects, useMoney, useOrgIndex } from "@/domain/hooks";
import { useStore } from "@/domain/store";
import { generateInsights, unitMetrics } from "@/domain/insights";
import { computeHealth } from "@/screens/programme/ProjectHealthTable";
import { isOpenProjectStatus } from "@/domain/status";
import type { AnalyticsSeed } from "@/domain/types";
import { cn } from "@/design/cn";

// ─── Palette helpers ─────────────────────────────────────────────────────────
const RAG_COLOR = {
  green: "bg-ok",
  amber: "bg-warn",
  red: "bg-bad",
  neutral: "bg-faint",
} as const;

const RAG_RING = {
  green: "ring-ok/30",
  amber: "ring-warn/30",
  red: "ring-bad/30",
  neutral: "ring-faint/30",
} as const;

// ─── Root ────────────────────────────────────────────────────────────────────
export function FunderInsightsDashboard() {
  const seed = useSeed();
  const money = useMoney();
  const projects = useProjects();
  const orgs = useOrgIndex();
  // Team tab route is workspace-scoped (/workspace/:workspaceId/projects/:id)
  // - funders have no such param in their own /funder/* routes, so this
  // follows the same "active workspace" convention useSeed()/useProjects()
  // above already rely on implicitly.
  const workspaceId = useStore((s) => s.activeWorkspaceId);
  const a = seed.analytics;

  const readyDisb = seed.disbursements.filter((d) => d.state === "ready_for_funder");
  const active = projects.filter((p) => isOpenProjectStatus(p.status));
  const health = active.map((p) => ({
    project: p,
    h: computeHealth(p, orgs[p.leadContractorId]?.name ?? "—"),
  }));
  const atRisk = health.filter((x) => x.h.overall === "red").length;
  const onWatch = health.filter((x) => x.h.overall === "amber").length;
  const onTrack = health.filter((x) => x.h.overall === "green").length;

  const evCoverage = active.length
    ? active.reduce((s, p) => s + (p.spent ? p.evidenced / p.spent : 1), 0) / active.length
    : 1;

  const insights = generateInsights(seed);
  const metrics = unitMetrics(seed);

  // Overall portfolio status
  const portfolioOk = atRisk === 0 && a.projection.projectedDateDriftDays <= 7;

  return (
    <div className="space-y-6">
      {/* ── Disbursement action alert ──────────────────────────────────────── */}
      {readyDisb.length > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-warn/30 bg-warnSoft px-4 py-3">
          <div className="flex items-center gap-3">
            <Inbox className="h-4 w-4 shrink-0 text-warnStrong" />
            <p className="text-sm font-medium text-warnStrong">
              {readyDisb.length} milestone disbursement{readyDisb.length > 1 ? "s" : ""} awaiting your approval
            </p>
          </div>
          <Link
            to="/funder/approvals"
            className="flex shrink-0 items-center gap-1 rounded-lg bg-warn/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-warn transition-colors"
          >
            Review now <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* ── Portfolio status headline ──────────────────────────────────────── */}
      <div className="rounded-xl border border-line bg-[#0A0E22] px-6 py-5 relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.8) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.8) 1px,transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="pointer-events-none absolute top-0 right-0 w-64 h-64 rounded-full bg-[#535EFF] opacity-10 blur-3xl translate-x-1/3 -translate-y-1/2" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {portfolioOk
                  ? <CheckCircle2 className="h-4 w-4 text-ok" />
                  : <AlertTriangle className="h-4 w-4 text-warn" />}
                <span className={cn("text-xs font-semibold uppercase tracking-widest", portfolioOk ? "text-ok" : "text-warnStrong")}>
                  {portfolioOk ? "Portfolio on track" : "Portfolio needs attention"}
                </span>
              </div>
              <p className="text-xl font-semibold text-white/90 tracking-tight leading-snug max-w-xl">
                {insights[0]?.title ?? "Delivery is broadly in line with plan across funded programmes."}
              </p>
              <p className="mt-2 text-sm text-white/45 max-w-lg leading-relaxed">
                {insights[0]?.body ?? "No material risks flagged this period."}
              </p>
            </div>
            <div className="hidden lg:flex flex-col items-end gap-1 shrink-0">
              <span className="text-[11px] text-white/30 uppercase tracking-wider">Active programmes</span>
              <span className="text-4xl font-bold tabular-nums text-white/90">{active.length}</span>
              <div className="flex items-center gap-2 mt-1">
                <StatusDot tone="ok" label={`${onTrack} on track`} />
                {onWatch > 0 && <StatusDot tone="warn" label={`${onWatch} watch`} />}
                {atRisk > 0 && <StatusDot tone="bad" label={`${atRisk} at risk`} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 4 KPI cards ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label="Evidence coverage"
          value={`${Math.round(evCoverage * 100)}%`}
          sub="of spend evidenced"
          icon={ShieldCheck}
          tone={evCoverage >= 0.8 ? "ok" : "warn"}
          bar={evCoverage}
        />
        <KpiCard
          label="Budget consumed"
          value={formatPct(a.budget.consumedPct, 0)}
          sub={`vs ${formatPct(a.budget.timeElapsedPct, 0)} time elapsed`}
          icon={TrendingUp}
          tone={a.budget.burnRateVsPlanPct > 0.05 ? "warn" : "ok"}
          bar={a.budget.consumedPct}
        />
        <KpiCard
          label="Schedule drift"
          value={`+${a.projection.projectedDateDriftDays}d`}
          sub="projected vs plan"
          icon={Clock}
          tone={a.projection.projectedDateDriftDays > 10 ? "warn" : "ok"}
        />
        <KpiCard
          label="Milestones on track"
          value={`${a.milestones.filter((m) => m.onTrack).length}/${a.milestones.length}`}
          sub="holding their plan"
          icon={CheckCircle2}
          tone={a.milestones.every((m) => m.onTrack) ? "ok" : "warn"}
        />
      </div>

      {/* ── Main 2-col ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        {/* Left: Programme health matrix */}
        <div className="space-y-3 xl:col-span-2">
          <SectionLabel>Programme health</SectionLabel>
          <div className="space-y-2">
            {health.map(({ project: p, h }) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-line bg-card px-4 py-3">
                <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full ring-4", RAG_COLOR[h.overall], RAG_RING[h.overall])} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                  <p className="text-xs text-faint">{orgs[p.leadContractorId]?.name ?? "—"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs tabular-nums font-medium text-ink">{formatPct(p.budgeted ? p.spent / p.budgeted : 0, 0)}</p>
                  <p className="text-[11px] text-faint">budget used</p>
                </div>
              </div>
            ))}
          </div>

          {/* AI Insights */}
          {insights.slice(1).map((ins) => (
            <div
              key={ins.id}
              className={cn(
                "rounded-xl border p-4 space-y-1.5",
                ins.kind === "outlier" ? "border-warn/30 bg-warnSoft" :
                ins.kind === "positive" ? "border-ok/30 bg-okSoft" :
                "border-line bg-card"
              )}
            >
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-[10px] font-semibold uppercase tracking-widest",
                  ins.kind === "outlier" ? "text-warnStrong" :
                  ins.kind === "positive" ? "text-okStrong" : "text-brand"
                )}>
                  {ins.kind === "outlier" ? "Watch" : ins.kind === "positive" ? "Positive" : "Note"}
                </span>
              </div>
              <p className="text-sm font-medium text-ink leading-snug">{ins.title}</p>
              <p className="text-xs text-mute leading-relaxed">{ins.body}</p>
            </div>
          ))}
        </div>

        {/* Right: schedule + budget breakdown */}
        <div className="space-y-5 xl:col-span-3">
          <div>
            <SectionLabel>Schedule</SectionLabel>
            <NarrativeCard
              variant="interpretation"
              title="On time?"
              body={(() => {
                const onTrackMs = a.milestones.filter((m) => m.onTrack).length;
                const worst = a.milestones.filter((m) => !m.onTrack).sort((x, y) =>
                  new Date(y.projectedDate).getTime() - new Date(x.projectedDate).getTime())[0];
                const driftDays = worst
                  ? Math.round((new Date(worst.projectedDate).getTime() - new Date(worst.plannedDate).getTime()) / 86_400_000)
                  : 0;
                return worst
                  ? `${onTrackMs} of ${a.milestones.length} milestones hold their plan. ${worst.name} is the schedule outlier, tracking ${driftDays} days behind. The overall programme projection absorbs this within the contingency buffer.`
                  : `All ${a.milestones.length} milestones are holding their plan with no material schedule risk this period.`;
              })()}
              figures={[
                { label: "Projected drift", value: `+${a.projection.projectedDateDriftDays} days`, tone: a.projection.projectedDateDriftDays > 5 ? "warn" : undefined },
                { label: "Milestones on time", value: `${a.milestones.filter((m) => m.onTrack).length} / ${a.milestones.length}` },
              ]}
            />
            <div className="mt-3">
              <MilestoneDriftChart milestones={a.milestones} />
            </div>
          </div>

          <div>
            <SectionLabel>Budget</SectionLabel>
            <NarrativeCard
              variant="interpretation"
              title="On budget?"
              body={`Spend is ${formatPct(a.budget.consumedPct, 0)} of the committed envelope against ${formatPct(a.budget.timeElapsedPct, 0)} of programme time, a burn rate ${formatPct(a.budget.burnRateVsPlanPct, 1)} ahead of plan. The projected variance of ${formatHumanCurrency(Math.round(a.projection.projectedOverBudgetPct * seed.programme.totalBudget))} sits within the contingency envelope.`}
              figures={[
                { label: "Budget consumed", value: formatPct(a.budget.consumedPct, 1) },
                { label: "Time elapsed", value: formatPct(a.budget.timeElapsedPct, 1) },
                { label: "Burn vs plan", value: `+${formatPct(a.budget.burnRateVsPlanPct, 1)}`, tone: "warn" },
              ]}
            />
            <div className="mt-3 rounded-xl border border-line bg-card p-4 space-y-3">
              <BudgetBar label="Budget consumed" value={a.budget.consumedPct} tone="brand" />
              <BudgetBar label="Time elapsed" value={a.budget.timeElapsedPct} tone="warn" />
            </div>
          </div>

          {/* Site-level pace table */}
          <div>
            <SectionLabel>Site pace</SectionLabel>
            {/* overflow-hidden on the outer div is only for the rounded
                corners - it was also clipping the table itself on a narrow
                viewport instead of letting it scroll (this is a 5-column
                dense table with no min-width, the exact "hand-rolled table
                with no horizontal-scroll container" gap DataTable already
                solves elsewhere in the app). The inner overflow-x-auto +
                table min-width restores that same escape hatch here. */}
            <div className="rounded-xl border border-line bg-card overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[32rem] text-sm">
                <thead>
                  <tr className="border-b border-hairline">
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-faint">Site</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-faint">Budget pace</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-faint">Evidence</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-faint">Drift</th>
                    <th className="px-4 py-2.5 text-right text-xs font-medium text-faint"><span className="sr-only">Team</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {metrics.map((m) => (
                    <tr key={m.projectId} className="hover:bg-subtle transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {m.outlier && <span className="h-1.5 w-1.5 rounded-full bg-warn shrink-0" />}
                          <span className={cn("font-medium", m.outlier ? "text-ink" : "text-mute")}>{m.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                        <span className={m.spentPct > 0.9 ? "text-bad font-semibold" : m.spentPct > 0.75 ? "text-warnStrong" : "text-ink"}>
                          {formatPct(m.spentPct, 0)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                        <span className={m.evidencePct < 0.7 ? "text-bad font-semibold" : m.evidencePct < 0.85 ? "text-warnStrong" : "text-okStrong"}>
                          {formatPct(m.evidencePct, 0)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                        {m.driftDays > 0 ? (
                          <Pill variant={m.driftDays > 14 ? "danger" : "warn"}>+{m.driftDays}d</Pill>
                        ) : (
                          <span className="text-okStrong font-medium">On plan</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link
                          to={`/workspace/${workspaceId}/projects/${m.projectId}`}
                          className="text-xs font-medium text-brand hover:underline"
                        >
                          Team
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Awaiting action ──────────────────────────────────────────────── */}
      {readyDisb.length > 0 && (
        <div>
          <SectionLabel>Awaiting your decision</SectionLabel>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {readyDisb.map((d) => {
              const ms = seed.milestones.find((m) => m.id === d.milestoneId);
              const basis = (d.basisAskIds ?? []).filter(Boolean);
              return (
                <Link
                  key={d.id}
                  to="/funder/approvals"
                  className="group rounded-xl border border-warn/25 bg-warnSoft p-4 hover:border-warn/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-warnStrong">Milestone disbursement</span>
                      <p className="mt-1 text-sm font-semibold text-ink">{ms?.name ?? "Milestone"}</p>
                      <p className="mt-1 text-xs text-mute">
                        Justified by {basis.length} confirmed spend ask{basis.length === 1 ? "" : "s"} with evidence attached.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold tabular-nums text-ink">{money(d.amount)}</p>
                      <p className="text-xs text-faint">tranche amount</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-warnStrong group-hover:gap-2 transition-all">
                    Review basis and approve <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <p className="shrink-0 text-[11px] font-semibold uppercase tracking-widest text-faint">{children}</p>
      <div className="h-px flex-1 bg-hairline" />
    </div>
  );
}

function StatusDot({ tone, label }: { tone: "ok" | "warn" | "bad"; label: string }) {
  const dot = tone === "ok" ? "bg-ok" : tone === "warn" ? "bg-warn" : "bg-bad";
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      <span className="text-[11px] text-white/40">{label}</span>
    </span>
  );
}

function KpiCard({
  label, value, sub, icon: Icon, tone, bar,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ElementType;
  tone: "ok" | "warn";
  bar?: number;
}) {
  const isOk = tone === "ok";
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-card">
      {/* Top accent bar — the single most scannable status signal */}
      <div className={cn("h-[3px]", isOk ? "bg-ok" : "bg-warn")} />
      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-medium leading-tight text-faint">{label}</span>
          <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", isOk ? "text-ok" : "text-warnStrong")} />
        </div>
        <div>
          <p className="text-3xl font-bold tabular-nums tracking-tight text-ink">{value}</p>
          <p className="mt-0.5 text-xs text-faint">{sub}</p>
        </div>
        {bar !== undefined && (
          <ProgressBar value={bar} tone={isOk ? "ok" : "warn"} />
        )}
      </div>
    </div>
  );
}

function BudgetBar({ label, value, tone }: { label: string; value: number; tone: "brand" | "warn" }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="text-mute">{label}</span>
        <span className="tabular-nums font-medium text-ink">{formatPct(value, 1)}</span>
      </div>
      <ProgressBar value={value} tone={tone} />
    </div>
  );
}

function MilestoneDriftChart({ milestones }: { milestones: AnalyticsSeed["milestones"] }) {
  const data = milestones.map((m) => ({
    name: m.name,
    drift: Math.round((new Date(m.projectedDate).getTime() - new Date(m.plannedDate).getTime()) / 86_400_000),
    onTrack: m.onTrack,
  }));
  return (
    <div className="rounded-xl border border-line bg-card p-4">
      <p className="mb-3 text-xs font-medium text-faint">Drift vs plan (days)</p>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} layout="vertical" margin={{ left: 16, right: 24, top: 0, bottom: 0 }}>
          <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-tertiary)" }} />
          <YAxis type="category" dataKey="name" width={120} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
          <RTooltip
            cursor={{ fill: "var(--bg-subtle)" }}
            contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid var(--border-default)", background: "var(--bg-card)" }}
            formatter={(v: number) => [`${v} days`, "drift"]}
          />
          <ReferenceLine x={0} stroke="var(--border-strong)" />
          <Bar dataKey="drift" radius={[0, 5, 5, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.onTrack ? "var(--status-success)" : "var(--status-warn)"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
